(() => {
  'use strict';

  /* =========================================================
     CART VARIANT SWITCHER

     The +/- UI changes PRODUCT VARIANTS, not Shopify quantity.

     Important integration rule:
     - The common variant replacement path uses ONE cart/update.js request:
       remove the old line and set/add the requested variant atomically.
     - Shopify returns the authoritative FINAL cart from that request.
     - That exact final cart is passed to Aura Free Gift before rendering.
     - If the gift state also changes, Aura finalizes it first and returns the
       newest sections. This keeps price, progress and gift synchronized while
       reducing the number of sequential network round trips.
     ========================================================= */

  const CONFIG = {
    sections: ['cart-drawer', 'cart-icon-bubble'],
    observerDelay: 10
  };

  const ROOT =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  function cartRoute(name, fallback) {
    if (window.routes && window.routes[name]) {
      return window.routes[name];
    }

    return `${ROOT}${fallback}`;
  }

  function getVariants(switcher) {
    const dataElement = switcher.querySelector('.cart-variant-data');
    if (!dataElement) return [];

    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('[Cart Variant] Invalid variant data', error);
      return [];
    }
  }

  function getCurrentIndex(switcher, variants) {
    const currentVariant = Number(switcher.dataset.currentVariant);
    const index = variants.findIndex(
      (variant) => Number(variant.id) === currentVariant
    );

    return index >= 0 ? index : 0;
  }

  function updateSwitcherUI(switcher, index, variants) {
    const variant = variants[index];
    if (!variant) return;

    const number = switcher.querySelector(
      '.cart-variant-switcher__number'
    );
    const name = switcher.querySelector(
      '.cart-variant-switcher__name'
    );
    const minus = switcher.querySelector(
      '.cart-variant-switcher__minus'
    );
    const plus = switcher.querySelector(
      '.cart-variant-switcher__plus'
    );

    if (number) number.textContent = index + 1;
    if (name) name.textContent = variant.title || '';
    if (minus) minus.disabled = index <= 0;
    if (plus) plus.disabled = index >= variants.length - 1;

    switcher.dataset.index = index;
    switcher.dataset.currentVariant = variant.id;
  }

  function initializeSwitcher(switcher) {
    if (switcher.dataset.initialized === 'true') return;

    const variants = getVariants(switcher);
    if (!variants.length) return;

    if (variants.length <= 1) {
      switcher.classList.add('dactive');
      switcher.dataset.initialized = 'true';
      return;
    }

    const currentIndex = getCurrentIndex(switcher, variants);
    updateSwitcherUI(switcher, currentIndex, variants);
    switcher.dataset.initialized = 'true';
  }

  function initializeAllSwitchers() {
    document
      .querySelectorAll('.cart-variant-switcher')
      .forEach(initializeSwitcher);
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* =========================================================
     APPLY FINAL CART SECTIONS

     Primary path uses the Aura renderer so the free-gift progress UI
     is repainted after the drawer HTML has been replaced.
     Fallback keeps this file usable even if Aura is disabled.
     ========================================================= */

  function applyCartSections(sections) {
    if (!sections || typeof sections !== 'object') return false;

    if (
      window.AuraFreeGift &&
      typeof window.AuraFreeGift.applySections === 'function'
    ) {
      return window.AuraFreeGift.applySections(sections);
    }

    let changed = false;

    if (sections['cart-drawer']) {
      const currentDrawer = document.querySelector('cart-drawer');
      const parsed = parseHTML(sections['cart-drawer']);
      const newDrawer = parsed.querySelector('cart-drawer');

      if (currentDrawer && newDrawer) {
        const currentInner = currentDrawer.querySelector('.drawer__inner');
        const newInner = newDrawer.querySelector('.drawer__inner');

        if (currentInner && newInner) {
          const scrollTop = currentInner.scrollTop;
          const wasOpen = currentDrawer.classList.contains('active');

          currentInner.replaceWith(newInner);
          currentDrawer.classList.toggle(
            'is-empty',
            newDrawer.classList.contains('is-empty')
          );

          if (wasOpen) currentDrawer.classList.add('active');

          const replacement = currentDrawer.querySelector('.drawer__inner');
          if (replacement) replacement.scrollTop = scrollTop;

          changed = true;
        }
      }
    }

    if (sections['cart-icon-bubble']) {
      const currentBubble = document.querySelector('#cart-icon-bubble');
      const parsed = parseHTML(sections['cart-icon-bubble']);
      const newBubble = parsed.querySelector('#cart-icon-bubble');

      if (currentBubble && newBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
        changed = true;
      }
    }

    return changed;
  }

  async function requestJSON(url, options) {
    const response = await fetch(url, options);

    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const message =
        data && (data.description || data.message)
          ? data.description || data.message
          : `Cart request failed (${response.status})`;
      throw new Error(message);
    }

    if (data && data.errors) {
      throw new Error(
        typeof data.errors === 'string'
          ? data.errors
          : 'Shopify returned a cart error'
      );
    }

    return data;
  }

  /* =========================================================
     FAST ATOMIC VARIANT REPLACEMENT

     cart/update.js can update multiple lines in one request and can add a
     variant ID that is not already in the cart. This replaces the previous
     add.js -> change.js two-request sequence for the normal switcher path.

     If the destination variant is already present as another paid cart line,
     update that exact line key to existing + moved quantity so behavior stays
     equivalent to add.js merging the variant.
     ========================================================= */
  function findExistingVariantSwitcher(switcher, variantId) {
    return Array.from(
      document.querySelectorAll('.cart-variant-switcher')
    ).find((candidate) => {
      if (!candidate || candidate === switcher) return false;

      return String(candidate.dataset.currentVariant || '') ===
        String(variantId);
    }) || null;
  }

  async function replaceVariantFast(switcher, variantId, quantity) {
    const oldKey = String(switcher.dataset.lineKey || '');
    const movedQuantity = Math.max(1, Number(quantity || 1));

    if (!oldKey) {
      throw new Error('Missing cart line key');
    }

    const updates = {
      [oldKey]: 0
    };

    const existing = findExistingVariantSwitcher(
      switcher,
      variantId
    );

    if (existing && existing.dataset.lineKey) {
      const existingQuantity = Math.max(
        0,
        Number(existing.dataset.quantity || 0)
      );

      updates[String(existing.dataset.lineKey)] =
        existingQuantity + movedQuantity;
    } else {
      /* update.js adds a new line when this variant ID is not in the cart. */
      updates[String(variantId)] = movedQuantity;
    }

    return requestJSON(
      cartRoute('cart_update_url', 'cart/update.js'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          updates,
          sections: CONFIG.sections,
          sections_url: window.location.pathname
        })
      }
    );
  }

  /* =========================================================
     FALLBACK DRAWER REFRESH

     Normally not used because cart/update.js returns bundled sections.
     ========================================================= */

  async function refreshCartDrawer() {
    const separator = window.location.pathname.includes('?') ? '&' : '?';
    const response = await fetch(
      `${window.location.pathname}${separator}sections=${CONFIG.sections.join(',')}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      }
    );

    if (!response.ok) throw new Error('Drawer refresh failed');

    const sections = await response.json();
    applyCartSections(sections);
    initializeAllSwitchers();
  }

  async function changeVariant(switcher, newVariant) {
    if (switcher.dataset.loading === 'true') return;

    const oldKey = switcher.dataset.lineKey;
    const quantity = Number(switcher.dataset.quantity || 1);

    if (!oldKey) {
      console.error('[Cart Variant] Missing line key');
      return;
    }

    switcher.dataset.loading = 'true';
    switcher.classList.add('is-loading');

    let variantTransactionStarted = false;

    if (
      window.AuraFreeGift &&
      typeof window.AuraFreeGift.beginVariantTransaction === 'function'
    ) {
      await window.AuraFreeGift.beginVariantTransaction();
      variantTransactionStarted = true;
    }

    try {
      /*
       * 1) Atomically replace the old cart line with the requested variant.
       * One request returns the authoritative FINAL cart + drawer sections.
       */
      let finalCart = await replaceVariantFast(
        switcher,
        newVariant.id,
        quantity
      );

      /*
       * 2) Reconcile the free gift against that exact final cart total.
       * If threshold was crossed, Aura repairs the checkout-safe gift order
       * and returns the newest final sections. No page reload is needed.
       */
      if (
        window.AuraFreeGift &&
        typeof window.AuraFreeGift.afterVariantChange === 'function'
      ) {
        finalCart = await window.AuraFreeGift.afterVariantChange(
          finalCart,
          CONFIG.sections
        );
      }

      /* 3) Paint ONLY the final synchronized state. */
      const rendered = applyCartSections(finalCart && finalCart.sections);

      if (!rendered) {
        await refreshCartDrawer();
      } else {
        initializeAllSwitchers();
      }

      if (
        variantTransactionStarted &&
        window.AuraFreeGift &&
        typeof window.AuraFreeGift.endVariantTransaction === 'function'
      ) {
        window.AuraFreeGift.endVariantTransaction({ synced: true });
        variantTransactionStarted = false;
      }

      if (
        window.AuraFreeGift &&
        typeof window.AuraFreeGift.paint === 'function'
      ) {
        window.AuraFreeGift.paint(finalCart);
      }

      /* Theme-level compatibility signal after the complete transaction. */
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            source: 'cart-variant',
            cart: finalCart
          }
        })
      );
    } catch (error) {
      console.error('[Cart Variant] Change failed:', error);

      /* Restore authoritative drawer/cart state after an error. */
      try {
        await refreshCartDrawer();
      } catch (refreshError) {
        console.error('[Cart Variant] Recovery refresh failed:', refreshError);
      }

      if (
        window.AuraFreeGift &&
        typeof window.AuraFreeGift.scheduleSync === 'function'
      ) {
        window.AuraFreeGift.scheduleSync(0);
      }
    } finally {
      if (
        variantTransactionStarted &&
        window.AuraFreeGift &&
        typeof window.AuraFreeGift.endVariantTransaction === 'function'
      ) {
        window.AuraFreeGift.endVariantTransaction({ synced: false });
      }

      /*
       * The original switcher may already have been replaced with the
       * final drawer HTML. Updating this old node is harmless.
       */
      switcher.dataset.loading = 'false';
      switcher.classList.remove('is-loading');
    }
  }

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest(
        '.cart-variant-switcher__button'
      );
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const switcher = button.closest('.cart-variant-switcher');
      if (!switcher || switcher.dataset.loading === 'true') return;

      const variants = getVariants(switcher);
      if (!variants.length) return;

      const currentIndex = Number(switcher.dataset.index || 0);
      let newIndex = currentIndex;

      if (button.classList.contains('cart-variant-switcher__plus')) {
        newIndex = currentIndex + 1;
      } else if (button.classList.contains('cart-variant-switcher__minus')) {
        newIndex = currentIndex - 1;
      }

      if (newIndex < 0 || newIndex >= variants.length) return;

      const newVariant = variants[newIndex];
      if (!newVariant) return;

      /* Immediate visual feedback; final drawer comes from Shopify. */
      updateSwitcherUI(switcher, newIndex, variants);
      await changeVariant(switcher, newVariant);
    },
    true
  );

  let observerTimer;
  const observer = new MutationObserver((mutations) => {
    let hasNewNodes = false;

    for (const mutation of mutations) {
      if (mutation.addedNodes && mutation.addedNodes.length) {
        hasNewNodes = true;
        break;
      }
    }

    if (!hasNewNodes) return;

    clearTimeout(observerTimer);
    observerTimer = setTimeout(
      initializeAllSwitchers,
      CONFIG.observerDelay
    );
  });

  function start() {
    initializeAllSwitchers();

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('cart:updated', initializeAllSwitchers);
    document.addEventListener('cart:refresh', initializeAllSwitchers);
    document.addEventListener('shopify:section:load', initializeAllSwitchers);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
